import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { Option, ProfilePatch, Vocabulary } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'

/** The intake questions, shared by Add lead and the contact card so both ask the
 *  same things in the same order. Every answer is optional: an officer takes what
 *  the borrower gives them on the first call and fills the rest in later.
 *
 *  The dropdowns come from the server (`/api/vocabulary`) rather than being
 *  duplicated here, so the form can't offer a value the server would reject. */
export function IntakeFields({ value, onChange, vocabulary }: {
  value: ProfilePatch
  onChange: (patch: ProfilePatch) => void
  vocabulary: Vocabulary | null
}) {
  const set = (key: keyof ProfilePatch) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      onChange({ ...value, [key]: e.target.value })

  const options = (list: Option[] | undefined) =>
    (list ?? []).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)

  const isPurchase = value.purpose === 'purchase' || value.purpose === undefined || value.purpose === null
  const priceLabel = isPurchase ? 'Target price' : "Today's home value"

  return (
    <div className="form-grid">
      <label className="fl">
        <span>What do they want to do?</span>
        <select className="field" value={String(value.purpose ?? '')} onChange={set('purpose')}>
          <option value="">Not asked yet</option>
          {options(vocabulary?.purpose)}
        </select>
      </label>
      <label className="fl">
        <span>When <span className="hint">— drives the follow-up</span></span>
        <select className="field" value={String(value.timeline ?? '')} onChange={set('timeline')}>
          <option value="">Not asked yet</option>
          {options(vocabulary?.timeline)}
        </select>
      </label>

      <label className="fl">
        <span>{priceLabel}</span>
        <input className="field mono" inputMode="decimal" placeholder="$"
          value={String(value.price_or_value ?? '')} onChange={set('price_or_value')} />
      </label>
      <label className="fl">
        <span>Loan amount</span>
        <input className="field mono" inputMode="decimal" placeholder="$"
          value={String(value.loan_amount ?? '')} onChange={set('loan_amount')} />
      </label>

      <label className="fl">
        <span>Down payment{isPurchase ? '' : <span className="hint"> — purchases only</span>}</span>
        <input className="field mono" inputMode="decimal" placeholder="$" disabled={!isPurchase}
          value={String(value.down_payment ?? '')} onChange={set('down_payment')} />
      </label>
      <label className="fl">
        {/* A self-reported band, never a pulled score: pulling credit is regulated
            and does not belong in a lead form. */}
        <span>Credit <span className="hint">— what they tell you</span></span>
        <select className="field" value={String(value.credit_band ?? '')} onChange={set('credit_band')}>
          <option value="">Not asked yet</option>
          {options(vocabulary?.credit_band)}
        </select>
      </label>

      <label className="fl">
        <span>Property use</span>
        <select className="field" value={String(value.property_use ?? '')} onChange={set('property_use')}>
          <option value="">Not asked yet</option>
          {options(vocabulary?.property_use)}
        </select>
      </label>
      <label className="fl">
        <span>How did they find us?</span>
        <select className="field" value={String(value.source ?? '')} onChange={set('source')}>
          <option value="">Not asked yet</option>
          {options(vocabulary?.source)}
        </select>
      </label>

      {value.source === 'referral' || value.source === 'past_client' ? (
        <div className="fl span-2">
          <span>Who sent them? <span className="hint">— this is what makes partner reporting work</span></span>
          <ReferrerPicker value={value} onChange={onChange} />
        </div>
      ) : null}

      {/* The house they're buying. Only on a purchase: on a refinance the subject
          property is where they already live, which the contact record holds. */}
      {isPurchase ? (
        <>
          <label className="fl span-2">
            <span>The house they’re buying <span className="hint">— once they’ve found it</span></span>
            <input className="field" value={String(value.property_street ?? '')}
              onChange={set('property_street')} placeholder="Street" />
          </label>
          <label className="fl">
            <span>City</span>
            <input className="field" value={String(value.property_city ?? '')}
              onChange={set('property_city')} />
          </label>
          <div className="form-grid" style={{ gap: 10 }}>
            <label className="fl">
              <span>State</span>
              <input className="field" maxLength={2} value={String(value.property_state ?? '')}
                onChange={set('property_state')} />
            </label>
            <label className="fl">
              <span>ZIP</span>
              <input className="field" value={String(value.property_zip ?? '')}
                onChange={set('property_zip')} />
            </label>
          </div>
        </>
      ) : null}

      <label className="checkrow span-2" style={{ opacity: isPurchase ? 1 : 0.5 }}>
        <input type="checkbox" checked={Boolean(value.first_time_buyer)} disabled={!isPurchase}
          onChange={(e) => onChange({ ...value, first_time_buyer: e.target.checked })} />
        <span>First-time buyer <span className="hint">— may qualify for down-payment assistance</span></span>
      </label>

      <label className="fl"><span>Co-borrower</span>
        <input className="field" value={String(value.co_borrower_name ?? '')}
          onChange={set('co_borrower_name')} placeholder="Name" /></label>
      <div className="fl">
        <span>Their phone or email</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <input className="field" type="tel" placeholder="Phone" style={{ flex: 1 }}
            value={String(value.co_borrower_phone ?? '')} onChange={set('co_borrower_phone')} />
          <input className="field" type="email" placeholder="Email" style={{ flex: 1 }}
            value={String(value.co_borrower_email ?? '')} onChange={set('co_borrower_email')} />
        </div>
      </div>

      <label className="fl span-2">
        <span>What are they trying to do? <span className="hint">— in their words</span></span>
        <textarea className="field" rows={2} style={{ minHeight: 54 }}
          value={String(value.goal_notes ?? '')} onChange={set('goal_notes')} />
      </label>
    </div>
  )
}

/** Find the person who sent this lead. A referrer already in the lead book is
 *  stored by contact id; an agent known only from loan files is stored by their
 *  person key, so the credit lands on the same record either way. */
function ReferrerPicker({ value, onChange }: {
  value: ProfilePatch
  onChange: (patch: ProfilePatch) => void
}) {
  const chosen = value.referred_by_contact_id || value.referred_by_person_key
  const [q, setQ] = useState('')
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setTerm(q.trim()), 200)
    return () => window.clearTimeout(t)
  }, [q])

  const { data } = useAsync(
    () => (term.length >= 2 ? api.search(term) : Promise.resolve(null)),
    [term],
  )

  // Close the list when focus leaves the whole control, not on every blur inside it.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  if (chosen) {
    const name = value.referred_by_name || value.referred_by_person_key || 'Selected partner'
    return (
      <span className="tag" style={{ alignSelf: 'flex-start', fontSize: 12.5, padding: '5px 6px 5px 11px' }}>
        <Icon name="handshake" size={14} /> {String(name)}
        <button aria-label="Clear referrer"
          onClick={() => onChange({ ...value, referred_by_contact_id: null, referred_by_person_key: null, referred_by_name: null })}>
          <Icon name="close" size={13} />
        </button>
      </span>
    )
  }

  const leads = data?.leads ?? []
  const people = data?.people ?? []

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <input
        className="field"
        placeholder="Search agents, past clients, anyone"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
      />
      {open && term.length >= 2 && (leads.length > 0 || people.length > 0) ? (
        <div className="menu" style={{ top: '100%', left: 0, right: 0, marginTop: 4, maxHeight: 220, overflowY: 'auto' }}>
          {leads.map((l) => (
            <button key={`c${l.id}`} onClick={() => {
              onChange({ ...value, referred_by_contact_id: l.id, referred_by_person_key: null, referred_by_name: l.full_name })
              setOpen(false)
            }}>
              <Icon name="person" size={15} />
              <span>{l.full_name ?? '—'}<span className="hint" style={{ display: 'block' }}>{l.city ?? 'in the lead book'}</span></span>
            </button>
          ))}
          {people.filter((p) => !p.contact_id).map((p) => (
            <button key={`p${p.person_key}`} onClick={() => {
              onChange({ ...value, referred_by_person_key: p.person_key, referred_by_contact_id: null, referred_by_name: p.name })
              setOpen(false)
            }}>
              <Icon name="badge" size={15} />
              <span>{p.name ?? '—'}<span className="hint" style={{ display: 'block' }}>{p.company ?? p.roles ?? 'on a loan file'}</span></span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** One shared fetch of the dropdown vocabularies for a screen that needs them. */
export function useVocabulary(enabled = true) {
  return useAsync(() => (enabled ? api.vocabulary() : Promise.resolve(null)), [enabled]).data
}
